/**
 * EPIC283-A centralized customer-language content contract.
 * Synthetic/discovery only — does not derive EPIC282 measurement truth.
 */

export type DisplayStateKey =
  | 'current'
  | 'current_with_limitations'
  | 'delayed'
  | 'stale'
  | 'never_measured'
  | 'scan_only_collecting'
  | 'unavailable'
  | 'readiness_unavailable'
  | 'empty'
  | 'blocked'
  | 'unauthorized'
  | 'failure';

export type CustomerNounCandidate = 'safety_issue' | 'recurring_condition';

export type ContentBlock = {
  label: string;
  message: string;
  limitation: string;
  primaryCta: string;
};

/** Terms that must never appear in default customer-facing strings. */
export const FORBIDDEN_CUSTOMER_TERMS = [
  'ConditionCurrentProjection',
  'OperationalMeasurementProjection',
  'measurement_bucket_id',
  'reason_code',
] as const;

const DISPLAY_STATE_CONTENT: Record<DisplayStateKey, ContentBlock> = {
  current: {
    label: 'Current',
    message: 'Updated through {asOf}',
    limitation: 'No material measurement limitations for this window.',
    primaryCta: 'Inspect attention',
  },
  current_with_limitations: {
    label: 'Current with limitations',
    message: 'Current through {asOf}, with coverage limitation',
    limitation: 'Camera coverage is partial for part of the shift.',
    primaryCta: 'View limitation',
  },
  delayed: {
    label: 'Delayed',
    message: 'Updates are delayed; latest measurement {asOf}',
    limitation: 'Source publication is behind the declared update mode.',
    primaryCta: 'View status',
  },
  stale: {
    label: 'Stale',
    message: 'Current site activity is unknown; last measured {asOf}',
    limitation: 'No usable measurement in the required freshness window.',
    primaryCta: 'Restore source or run approved measurement',
  },
  never_measured: {
    label: 'Never measured',
    message: 'Measurement has not started for this scope',
    limitation: 'Activation and first measurement are incomplete.',
    primaryCta: 'Complete activation',
  },
  scan_only_collecting: {
    label: 'Scan collecting',
    message:
      'Current scan is collecting; latest completed measurement {asOf}. Next update after the current scan completes.',
    limitation: 'Scan-completion producer; not an incremental current view.',
    primaryCta: 'View scan',
  },
  unavailable: {
    label: 'Temporarily unavailable',
    message: 'Measurement is temporarily unavailable; no optimistic cached claim',
    limitation: 'Site-state API or projection is not available right now.',
    primaryCta: 'Retry',
  },
  readiness_unavailable: {
    label: 'Readiness unavailable',
    message: 'Now cannot be shown as a trusted current view yet',
    limitation:
      'Required readiness fields are missing or blocked. Measurement currentness is not claimed.',
    primaryCta: 'View readiness status',
  },
  empty: {
    label: 'Empty',
    message: 'No qualifying issues in this scope for the selected window',
    limitation: 'Absence of qualifying activity is not a safety claim.',
    primaryCta: 'Review measurement coverage',
  },
  blocked: {
    label: 'Blocked',
    message: 'This view is blocked until setup or measurement is ready',
    limitation: 'Required site setup or measurement readiness is incomplete.',
    primaryCta: 'Open site setup',
  },
  unauthorized: {
    label: 'Unauthorized',
    message: 'You do not have access to this site’s safety views',
    limitation: 'Role or site scope does not include this experience.',
    primaryCta: 'Request access',
  },
  failure: {
    label: 'Unavailable',
    message: 'Measurement is temporarily unavailable',
    limitation: 'An unexpected failure prevented loading measurement status.',
    primaryCta: 'Retry / contact support',
  },
};

const CUSTOMER_NOUN_LABELS: Record<CustomerNounCandidate, string> = {
  safety_issue: 'Safety issue',
  recurring_condition: 'Recurring pattern',
};

const COMPARABILITY_LABELS = {
  valid: 'Valid comparison',
  limited: 'Limited comparison',
  cannot_compare: 'Cannot compare',
  inconclusive: 'Inconclusive — workflow complete, not a favorable change',
} as const;

const PROOF_LANGUAGE =
  'Verified descriptive change under comparable observed conditions';

export function customerNounLabel(noun: CustomerNounCandidate): string {
  return CUSTOMER_NOUN_LABELS[noun];
}

export function displayStateContent(state: DisplayStateKey): ContentBlock {
  return DISPLAY_STATE_CONTENT[state];
}

export function formatDisplayMessage(state: DisplayStateKey, asOf: string): string {
  return displayStateContent(state).message.split('{asOf}').join(asOf);
}

export function comparabilityLabel(
  key: keyof typeof COMPARABILITY_LABELS,
): string {
  return COMPARABILITY_LABELS[key];
}

export function proofSuccessLanguage(): string {
  return PROOF_LANGUAGE;
}

export function setupBoundaryCopy(): ContentBlock {
  return {
    label: 'Site setup',
    message: 'Connect sources, shifts, and measurement settings before go-live',
    limitation: 'Activation owner: customer site admin with Edgentik support.',
    primaryCta: 'Review setup checklist',
  };
}

export function attentionBoundaryCopy(count: number): ContentBlock {
  return {
    label: 'Attention',
    message: `${count} item${count === 1 ? '' : 's'} need a decision`,
    limitation: 'Entry placeholder only — queue and notifications are later slices.',
    primaryCta: 'Open attention (prototype)',
  };
}

/** Collect every default customer string for contract tests. */
export function allDefaultCustomerStrings(): string[] {
  const fromStates = Object.values(DISPLAY_STATE_CONTENT).flatMap((block) => [
    block.label,
    block.message,
    block.limitation,
    block.primaryCta,
  ]);
  return [
    ...fromStates,
    ...Object.values(CUSTOMER_NOUN_LABELS),
    ...Object.values(COMPARABILITY_LABELS),
    PROOF_LANGUAGE,
    setupBoundaryCopy().message,
    setupBoundaryCopy().limitation,
    attentionBoundaryCopy(0).message,
    attentionBoundaryCopy(0).limitation,
  ];
}

export function findForbiddenCustomerTerms(text: string): string[] {
  return FORBIDDEN_CUSTOMER_TERMS.filter((term) => text.includes(term));
}

/** Bare "Live" as a measurement-state label is forbidden (word-boundary). */
export function containsBareLiveMeasurementLabel(text: string): boolean {
  return /\bLive\b/.test(text);
}
